import { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/components/auth";
import { useAdminAlert } from "@/hooks/use-admin-alert";
import { usePermission } from "@/hooks/use-permission";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  filterNavItems,
  IS_DEV_MODE,
  getFeatureStatus,
  pathToFeatureKey,
} from "@/config/features.config";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Wallet,
  Building2,
  Gift,
  Settings,
  ChevronLeft,
  ChevronRight,
  FileText,
  FileImage,
  Shield,
  TrendingUp,
  Calendar,
  Handshake,
  Link2,
  DollarSign,
  BarChart3,
  Banknote,
  Gamepad2,
  Layers,
  Server,
  Sliders,
  Clock,
  KeyRound,
  History,
  RefreshCcw,
  Megaphone,
  Image,
  Send,
  Layout,
  Smartphone,
  MessageSquare,
  Mail,
  Bell,
  MousePointer,
  Crown,
  Zap,
} from "lucide-react";

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

interface NavItem {
  titleKey: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  // ═══════════════════════════════════════════
  // 📊 หน้าหลัก
  // ═══════════════════════════════════════════
  {
    titleKey: "menu.dashboard",
    href: "/dashboard",
    icon: <LayoutDashboard className="h-5 w-5" />,
  },

  // ═══════════════════════════════════════════
  // 💰 จัดการสมาชิกและการเงิน (Core Operations)
  // ═══════════════════════════════════════════
  {
    titleKey: "menu.members",
    href: "/members",
    icon: <Users className="h-5 w-5" />,
    // badge จะถูก inject จาก notification counts
  },
  {
    titleKey: "menu.transactions",
    href: "/transactions",
    icon: <CreditCard className="h-5 w-5" />,
    children: [
      {
        titleKey: "menu.smartCashier",
        href: "/transactions/smart-cashier",
        icon: <Zap className="h-4 w-4" />,
      },
      // ✅ ซ่อนเมนู "รอดำเนินการ" และ "ประวัติทั้งหมด" ตามที่ผู้ใช้ขอ
      // {
      //   titleKey: 'menu.pendingTransactions',
      //   href: '/transactions/pending',
      //   icon: <Clock className="h-4 w-4" />,
      //   // badge จะถูก inject จาก notification counts
      // },
      // {
      //   titleKey: 'menu.transactionHistory',
      //   href: '/transactions/history',
      //   icon: <History className="h-4 w-4" />,
      // },
      {
        titleKey: "menu.slips",
        href: "/transactions/slips",
        icon: <FileImage className="h-4 w-4" />,
      },
    ],
  },
  {
    titleKey: "menu.banks",
    href: "/banks",
    icon: <Building2 className="h-5 w-5" />,
  },
  {
    titleKey: "menu.promotions",
    href: "/promotions",
    icon: <Gift className="h-5 w-5" />,
  },
  {
    titleKey: "menu.lossback",
    href: "/lossback",
    icon: <RefreshCcw className="h-5 w-5" />,
    children: [
      {
        titleKey: "menu.lossbackManage",
        href: "/lossback/manage",
        icon: <RefreshCcw className="h-4 w-4" />,
      },
      {
        titleKey: "menu.lossbackAuto",
        href: "/lossback/auto",
        icon: <Zap className="h-4 w-4" />,
      },
      {
        titleKey: "menu.lossbackHistory",
        href: "/lossback/records",
        icon: <History className="h-4 w-4" />,
      },
    ],
  },
  {
    titleKey: "menu.member_groups",
    href: "/member-groups",
    icon: <Crown className="h-5 w-5" />,
  },

  // ═══════════════════════════════════════════
  // 🎮 เกมและการตลาด (Content & Marketing)
  // ═══════════════════════════════════════════
  {
    titleKey: "menu.games",
    href: "/games",
    icon: <Gamepad2 className="h-5 w-5" />,
    children: [
      {
        titleKey: "menu.gameList",
        href: "/games/list",
        icon: <Gamepad2 className="h-4 w-4" />,
      },
      {
        titleKey: "menu.gameProviders",
        href: "/games/providers",
        icon: <Server className="h-4 w-4" />,
      },
      {
        titleKey: "menu.gameCategories",
        href: "/games/categories",
        icon: <Layers className="h-4 w-4" />,
      },
      {
        titleKey: "menu.gameSettings",
        href: "/games/settings",
        icon: <Sliders className="h-4 w-4" />,
      },
      {
        titleKey: "menu.gameReport",
        href: "/games/report",
        icon: <BarChart3 className="h-4 w-4" />,
      },
    ],
  },
  {
    titleKey: "menu.marketing",
    href: "/marketing",
    icon: <Megaphone className="h-5 w-5" />,
    children: [
      {
        titleKey: "menu.marketingBanners",
        href: "/marketing/banners",
        icon: <Image className="h-4 w-4" />,
      },
      {
        titleKey: "menu.marketingCampaigns",
        href: "/marketing/campaigns",
        icon: <Send className="h-4 w-4" />,
      },
      {
        titleKey: "menu.marketingPopups",
        href: "/marketing/popups",
        icon: <Layout className="h-4 w-4" />,
      },
      {
        titleKey: "menu.marketingSms",
        href: "/marketing/sms",
        icon: <Smartphone className="h-4 w-4" />,
      },
      {
        titleKey: "menu.marketingLine",
        href: "/marketing/line",
        icon: <MessageSquare className="h-4 w-4" />,
      },
      {
        titleKey: "menu.marketingEmail",
        href: "/marketing/email",
        icon: <Mail className="h-4 w-4" />,
      },
      {
        titleKey: "menu.marketingPush",
        href: "/marketing/push",
        icon: <Bell className="h-4 w-4" />,
      },
      {
        titleKey: "menu.marketingLanding",
        href: "/marketing/landing",
        icon: <MousePointer className="h-4 w-4" />,
      },
    ],
  },
  {
    titleKey: "menu.affiliates",
    href: "/affiliates",
    icon: <Handshake className="h-5 w-5" />,
    children: [
      {
        titleKey: "menu.affiliateList",
        href: "/affiliates/list",
        icon: <Users className="h-4 w-4" />,
      },
      {
        titleKey: "menu.affiliateCommissions",
        href: "/affiliates/commissions",
        icon: <DollarSign className="h-4 w-4" />,
      },
      {
        titleKey: "menu.affiliateLinks",
        href: "/affiliates/links",
        icon: <Link2 className="h-4 w-4" />,
      },
      {
        titleKey: "menu.affiliatePayouts",
        href: "/affiliates/payouts",
        icon: <Banknote className="h-4 w-4" />,
      },
      {
        titleKey: "menu.affiliateReport",
        href: "/affiliates/report",
        icon: <BarChart3 className="h-4 w-4" />,
      },
    ],
  },

  // ═══════════════════════════════════════════
  // 📈 รายงานและวิเคราะห์ (Analytics)
  // ═══════════════════════════════════════════
  {
    titleKey: "menu.reports",
    href: "/reports",
    icon: <TrendingUp className="h-5 w-5" />,
    children: [
      {
        titleKey: "menu.reportMonthly",
        href: "/reports/monthly",
        icon: <Calendar className="h-4 w-4" />,
      },
      {
        titleKey: "menu.reportFinancial",
        href: "/reports/financial",
        icon: <TrendingUp className="h-4 w-4" />,
      },
      {
        titleKey: "menu.reportMembers",
        href: "/reports/members",
        icon: <Users className="h-4 w-4" />,
      },
      {
        titleKey: "menu.reportTransactions",
        href: "/reports/transactions",
        icon: <CreditCard className="h-4 w-4" />,
      },
      {
        titleKey: "menu.reportBanks",
        href: "/reports/banks",
        icon: <Building2 className="h-4 w-4" />,
      },
    ],
  },

  // ═══════════════════════════════════════════
  // ⚙️ ระบบและผู้ดูแล (System Admin)
  // ═══════════════════════════════════════════
  {
    titleKey: "menu.admins",
    href: "/admins",
    icon: <Shield className="h-5 w-5" />,
  },
  {
    titleKey: "menu.acl",
    href: "/acl",
    icon: <KeyRound className="h-5 w-5" />,
  },
  {
    titleKey: "menu.cron_jobs",
    href: "/cron-jobs",
    icon: <Clock className="h-5 w-5" />,
  },
  {
    titleKey: "menu.auditLog",
    href: "/audit",
    icon: <FileText className="h-5 w-5" />,
  },
  {
    titleKey: "menu.devices",
    href: "/devices",
    icon: <Smartphone className="h-5 w-5" />,
  },
  {
    titleKey: "menu.changelog",
    href: "/changelog",
    icon: <History className="h-5 w-5" />,
  },
  {
    titleKey: "menu.settings",
    href: "/settings",
    icon: <Settings className="h-5 w-5" />,
  },
];

export function Sidebar({ collapsed, onCollapse }: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { sessionToken } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  
  // ✅ Permission hook
  const { canAccessMenu, canAccessSubmenu } = usePermission();

  // ✅ Admin Alert counts (แยกจาก Member Notification)
  const { counts } = useAdminAlert({
    enabled: !!sessionToken, // ✅ เปิดใช้งานเมื่อมี session token
    interval: 30000, // 30 seconds
    soundEnabled: false, // ไม่เล่นเสียงใน sidebar
  });

  // Filter nav items ตาม feature flags, permissions และ inject notification counts
  const filteredNavItems = useMemo(() => {
    let items = filterNavItems(navItems);

    // ✅ Filter ตาม permission
    items = items.filter((item) => {
      // ตรวจสอบว่าเข้าถึงเมนูหลักได้หรือไม่
      if (!canAccessMenu(item.href)) {
        return false;
      }

      // ถ้ามี children ให้ filter children ตาม permission
      if (item.children && item.children.length > 0) {
        const filteredChildren = item.children.filter((child) =>
          canAccessSubmenu(item.href, child.href)
        );
        
        // ถ้าไม่มี children ที่เข้าถึงได้เลย ให้ซ่อนเมนูหลักด้วย
        if (filteredChildren.length === 0) {
          return false;
        }
        
        // อัพเดท children
        item.children = filteredChildren;
      }

      return true;
    });

    // ✅ Inject notification counts into menu items
    return items.map((item) => {
      if (item.href === "/transactions/pending") {
        const total = (counts.pendingDeposits ?? 0) + (counts.pendingWithdraws ?? 0);
        return {
          ...item,
          badge: total > 0 ? total : undefined,
        };
      }
      if (item.href === "/members") {
        return {
          ...item,
          badge: counts.newMembers || undefined,
        };
      }
      return item;
    });
  }, [counts, canAccessMenu, canAccessSubmenu]);

  const toggleExpand = (href: string) => {
    setExpandedItems((prev) =>
      prev.includes(href) ? prev.filter((h) => h !== href) : [...prev, href]
    );
  };

  const isActive = (href: string) => {
    return (
      location.pathname === href || location.pathname.startsWith(href + "/")
    );
  };

  const NavItemComponent = ({
    item,
    depth = 0,
  }: {
    item: NavItem;
    depth?: number;
  }) => {
    const active = isActive(item.href);
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.href);
    const title = t(item.titleKey);

    // Get feature status for badge (dev mode only)
    const featureKey = pathToFeatureKey(item.href);
    const featureStatus = getFeatureStatus(featureKey);
    const showDevBadge = IS_DEV_MODE && featureStatus !== "ready";

    const content = (
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          active && "bg-sidebar-primary text-sidebar-primary-foreground",
          !active && "text-sidebar-foreground/70",
          depth > 0 && "ml-6 text-xs",
          collapsed && "justify-center px-2"
        )}
      >
        <span
          className={cn(
            "flex items-center justify-center shrink-0",
            active
              ? "text-sidebar-primary-foreground"
              : "text-sidebar-foreground/50"
          )}
        >
          {item.icon}
        </span>
        {!collapsed && (
          <>
            <span className="flex-1 truncate">{title}</span>
            {/* Dev/Beta Badge - แสดงเฉพาะ dev mode */}
            {showDevBadge && (
              <Badge
                variant="outline"
                className={cn(
                  "ml-auto text-[9px] px-1 py-0 border-0",
                  featureStatus === "dev" && "bg-blue-500/20 text-blue-400",
                  featureStatus === "beta" && "bg-amber-500/20 text-amber-400"
                )}
              >
                {featureStatus === "dev" ? "DEV" : "BETA"}
              </Badge>
            )}
            {/* Item badge (เช่น จำนวน pending) */}
            {item.badge && !showDevBadge && (
              <Badge
                variant="secondary"
                className="ml-auto text-[10px] px-1.5 py-0 bg-primary/10 text-primary"
              >
                {item.badge}
              </Badge>
            )}
            {hasChildren && (
              <ChevronRight
                className={cn(
                  "h-4 w-4 transition-transform text-sidebar-foreground/50",
                  isExpanded && "rotate-90"
                )}
              />
            )}
          </>
        )}
      </div>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              to={hasChildren ? "#" : item.href}
              onClick={hasChildren ? () => toggleExpand(item.href) : undefined}
            >
              {content}
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            {title}
            {item.badge && <Badge variant="secondary">{item.badge}</Badge>}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <div>
        {hasChildren ? (
          <button
            onClick={() => toggleExpand(item.href)}
            className="w-full text-left"
          >
            {content}
          </button>
        ) : (
          <Link to={item.href}>{content}</Link>
        )}
        {hasChildren && isExpanded && !collapsed && (
          <div className="mt-1 space-y-1">
            {item.children!.map((child) => (
              <NavItemComponent
                key={child.href}
                item={child}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen border-r border-sidebar-border bg-sidebar transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          "flex h-16 items-center border-b border-sidebar-border px-4",
          collapsed ? "justify-center" : "gap-3"
        )}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
          <Wallet className="h-6 w-6" />
        </div>
        {!collapsed && (
          <div>
            <h1 className="text-lg font-bold text-foreground">ADMIN</h1>
            <p className="text-xs text-muted-foreground">Control Panel</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="h-[calc(100vh-8rem)] px-3 py-4">
        <nav className="space-y-1">
          {filteredNavItems.map((item) => (
            <NavItemComponent key={item.href} item={item} />
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-sidebar-border p-3">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "w-full justify-start text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent",
            collapsed && "justify-center"
          )}
          onClick={() => onCollapse(!collapsed)}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4 mr-2" />
              <span>{t("menu.collapse")}</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}
